// @noflow
import { bulbModes, deviceTypes, deviceModes } from '~/core/constants/devices';

import { darken } from 'polished';
import ColorPicker from '~/components/bulbs/color-tuneable';
import { convertTemperatureToRGB, hsvToRgb, hsvToRgbString } from '~/core/utils/colour';
import { debounce } from 'lodash/fp';
import { fromJS } from 'immutable';
import messages from './intl';
import { PickerContainer } from './style';
import * as React from 'react';
import theme from '~/core/themes/main';

const maxSaturation = 99;

export const getValueColor = ({ value }) => {
    const colourTemperature = value.get('colourTemperature');
    const isWhiteMode = value.get('colourMode', bulbModes.white) === bulbModes.white;

    if (value.get('status') === deviceModes.OFF || (isWhiteMode && !colourTemperature)) {
        return theme.mediumGrey;
    }

    return isWhiteMode
        ? // darken the colour to make it easier to read
          darken(0.2, convertTemperatureToRGB(colourTemperature))
        : hsvToRgbString({ h: value.get('hue'), s: value.get('saturation'), v: 100 });
};

export const getValueLabel = ({ formatMessage, value }) => {
    const isWhiteMode = value.get('colourMode', bulbModes.white) === bulbModes.white;
    const brightness = value.get('brightness');

    if (value.get('status') === deviceModes.OFF || (isWhiteMode && !brightness)) {
        return formatMessage(messages.off);
    }
    return `${isWhiteMode ? brightness : value.get('value')}%`;
};

export const hasPicker = () => true;

export const getPicker = ({ config, value, updateValue }) => {
    const defaultBrightness = config.getIn(['products', 'colourtuneablelight', 'defaultBrightness']);
    const defaultTemperature = config.getIn(['products', 'colourtuneablelight', 'defaultTemperature']);
    const defaultHue = config.getIn(['products', 'colourtuneablelight', 'defaultHue']);
    const defaultSaturation = config.getIn(['products', 'colourtuneablelight', 'defaultSaturation']);

    const isOn = value.get('status') === 'ON';
    const colourTemperature = value.get('colourTemperature') || defaultTemperature;
    const colourMode = value.get('colourMode', bulbModes.colour);
    const isWhite = colourMode === bulbModes.white;
    const brightness = value.get(isWhite ? 'brightness' : 'value', defaultBrightness);
    const hue = value.get('hue', defaultHue);
    const saturation = value.get('saturation') || defaultSaturation;
    const hsv = {
        h: hue,
        s: value.get('saturation') || saturation,
        v: 100
    };
    const { red, green, blue } = hsvToRgb(hsv);
    const rgb = [red, green, blue];

    const brightnessChanged = newValue => updateValue(value.set(isWhite ? 'brightness' : 'value', newValue));
    const toggleLightbulb = () =>
        isOn
            ? updateValue(value.clear().set('status', 'OFF'))
            : updateValue(
                  fromJS({
                      status: 'ON',
                      value: brightness,
                      hue: hue,
                      saturation: saturation,
                      colourMode: bulbModes.colour
                  })
              );
    const changeMode = ({ mode }) =>
        mode === bulbModes.white
            ? updateValue(
                  fromJS({
                      status: 'ON',
                      brightness: brightness,
                      colourTemperature: colourTemperature,
                      colourMode: bulbModes.white
                  })
              )
            : updateValue(
                  fromJS({
                      status: 'ON',
                      value: brightness,
                      hue: hue,
                      saturation: saturation,
                      colourMode: bulbModes.colour
                  })
              );
    const colourChanged = debounce(
        1000,
        ({ hsv, valueInRange }) =>
            colourMode === bulbModes.colour
                ? updateValue(
                      value.withMutations(value =>
                          value.set('hue', hsv.h).set('saturation', Math.min(hsv.s, maxSaturation))
                      )
                  )
                : updateValue(value.set('colourTemperature', valueInRange))
    );

    return (
        <PickerContainer>
            <ColorPicker
                bulbBrightness={brightness}
                bulbColorMode={colourMode}
                bulbType={deviceTypes.COLOR_TUNEABLE_LIGHT}
                changeColor={colourChanged}
                changeColorMode={changeMode}
                colourStartRadians={3.52}
                colourStopRadians={5.9}
                delayRendering={500}
                handleRadiusPercent={5}
                isCustomRange={isWhite}
                isOn={isOn}
                isSmall
                isTuneable
                pickerDepthPercent={5}
                rangeValue={colourTemperature}
                rgb={rgb}
                toggleLightbulb={toggleLightbulb}
                valueChanged={brightnessChanged}
            />
        </PickerContainer>
    );
};

export const defaultEventValue = { status: 'OFF' };

export const getDefaultSchedule = () => ({
    monday: [
        {
            value: {
                status: 'ON',
                brightness: 100,
                colourTemperature: 2700,
                colourMode: bulbModes.white
            },
            start: 390
        },
        { value: { status: 'OFF' }, start: 510 },
        {
            value: {
                status: 'ON',
                brightness: 100,
                colourTemperature: 2700,
                colourMode: bulbModes.white
            },
            start: 960
        },
        { value: { status: 'OFF' }, start: 1290 }
    ],
    tuesday: [
        {
            value: {
                status: 'ON',
                brightness: 100,
                colourTemperature: 2700,
                colourMode: bulbModes.white
            },
            start: 390
        },
        { value: { status: 'OFF' }, start: 510 },
        {
            value: {
                status: 'ON',
                brightness: 100,
                colourTemperature: 2700,
                colourMode: bulbModes.white
            },
            start: 960
        },
        { value: { status: 'OFF' }, start: 1290 }
    ],
    wednesday: [
        {
            value: {
                status: 'ON',
                brightness: 100,
                colourTemperature: 2700,
                colourMode: bulbModes.white
            },
            start: 390
        },
        { value: { status: 'OFF' }, start: 510 },
        {
            value: {
                status: 'ON',
                brightness: 100,
                colourTemperature: 2700,
                colourMode: bulbModes.white
            },
            start: 960
        },
        { value: { status: 'OFF' }, start: 1290 }
    ],
    thursday: [
        {
            value: {
                status: 'ON',
                brightness: 100,
                colourTemperature: 2700,
                colourMode: bulbModes.white
            },
            start: 390
        },
        { value: { status: 'OFF' }, start: 510 },
        {
            value: {
                status: 'ON',
                brightness: 100,
                colourTemperature: 2700,
                colourMode: bulbModes.white
            },
            start: 960
        },
        { value: { status: 'OFF' }, start: 1290 }
    ],
    friday: [
        {
            value: {
                status: 'ON',
                brightness: 100,
                colourTemperature: 2700,
                colourMode: bulbModes.white
            },
            start: 390
        },
        { value: { status: 'OFF' }, start: 510 },
        {
            value: {
                status: 'ON',
                brightness: 100,
                colourTemperature: 2700,
                colourMode: bulbModes.white
            },
            start: 960
        },
        { value: { status: 'OFF' }, start: 1290 }
    ],
    saturday: [
        {
            value: {
                status: 'ON',
                brightness: 100,
                colourTemperature: 2700,
                colourMode: bulbModes.white
            },
            start: 390
        },
        { value: { status: 'OFF' }, start: 510 },
        {
            value: {
                status: 'ON',
                brightness: 100,
                colourTemperature: 2700,
                colourMode: bulbModes.white
            },
            start: 960
        },
        { value: { status: 'OFF' }, start: 1290 }
    ],
    sunday: [
        {
            value: {
                status: 'ON',
                brightness: 100,
                colourTemperature: 2700,
                colourMode: bulbModes.white
            },
            start: 390
        },
        { value: { status: 'OFF' }, start: 510 },
        {
            value: {
                status: 'ON',
                brightness: 100,
                colourTemperature: 2700,
                colourMode: bulbModes.white
            },
            start: 960
        },
        { value: { status: 'OFF' }, start: 1290 }
    ]
});



// WEBPACK FOOTER //
// ./src/components/widgets/schedule/schedule-type/color-tuneable-light/index.js